import { Vertical } from "@/components/standard/BFLayout"
import { Linking, Pressable, ScrollView } from "react-native"
import { Body, H2, H5, Tiny } from "@/components/standard/BFText"

import { LoanProductCard } from "@/screens/prequal/components/LoanProductCard"
import GoldTrophy from "@/assets/clipart/gold-standard-trophy.svg"
import DoubleDouble from "@/assets/clipart/double-double-bills.svg"
import Rocket from "@/assets/clipart/rocket-product.svg"
import Keys from "@/assets/clipart/refi-keys.svg"
import { useUserQualifiedForContactLink } from "@/hooks/useUserQualifiedForContactLink"
import { router } from "expo-router"
import { loanProductsQuestionClicked } from "@/analytics/events/loans"
import * as React from "react"
import { useCallback, useEffect, useRef } from "react"
import {
  BottomSheetBackdrop,
  type BottomSheetBackdropProps,
  BottomSheetModal,
  BottomSheetView,
} from "@gorhom/bottom-sheet"
import { BFColors } from "@/constants/Colors"
import { PrimaryButton } from "@/components/buttons/CommonButtons"
import { useSafeAreaInsets } from "react-native-safe-area-context"

export const ProductPopup = ({
  visible,
  onClose,
}: {
  visible: boolean
  onClose: (moveOn: boolean) => void
}) => {
  const bottomSheetModalRef = useRef<BottomSheetModal>(null)
  const qualifiedForContactLink = useUserQualifiedForContactLink()
  const insets = useSafeAreaInsets()

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        appearsOnIndex={0}
        disappearsOnIndex={-1}
        onPress={() => {
          onClose(false)
        }}
      />
    ),
    []
  )

  useEffect(() => {
    if (!visible) {
      bottomSheetModalRef.current?.dismiss()
      onClose(false)
    } else {
      bottomSheetModalRef.current?.present()
    }
  }, [visible])

  return (
    <BottomSheetModal
      ref={bottomSheetModalRef}
      backdropComponent={renderBackdrop}
      backgroundStyle={{ backgroundColor: "#FFFFFF" }}
      handleIndicatorStyle={{ backgroundColor: BFColors.offWhite }}
      enableDynamicSizing={true}
    >
      <BottomSheetView className={"flex-1 px-6"}>
        <Vertical
          className={"justify-between flex-1"}
          style={{ paddingBottom: insets.bottom }}
        >
          <Vertical className={"gap-2 mt-10"}>
            <Vertical className={"gap-2"}>
              <H2 className={"text-center"} color={"primary"}>
                No property, yet?
              </H2>
              <Body className={"text-center"} color={"primary"}>
                We need an address to provide a quote, but here are loan options
                that may be available once you’re ready:
              </Body>
            </Vertical>
            <ScrollView
              horizontal={true}
              contentContainerClassName={"gap-5 flex-row"}
              className={"max-h-[220px]"}
            >
              <LoanProductCard
                className={"w-[241px] h-[156px]"}
                name={"The Standard"}
                icon={<GoldTrophy width={60} height={60} />}
                description={
                  "Our core capital product, moderate leverage to boost total profit"
                }
                ltc={"85%"}
                interest={"from 9.00%"}
                origination={"from 1.25%"}
                noCta={true}
              />
              <LoanProductCard
                className={"w-[241px] h-[156px]"}
                name={"Zero Gravity"}
                icon={<Rocket width={60} height={60} />}
                description={
                  "No out of pocket lender fees, no interest payments for 6 months"
                }
                ltc={"96%"}
                interest={"from 11.75%"}
                origination={"2.00%"}
                noCta={true}
              />
              <LoanProductCard
                className={"w-[241px] h-[156px]"}
                icon={<DoubleDouble width={60} height={60} />}
                name={"Double Double"}
                description={
                  "Maximize your leverage to increase ROI on high-quality deals"
                }
                ltc={"93%"}
                interest={"from 12.75%"}
                origination={"2.00%"}
                noCta={true}
              />
              <LoanProductCard
                className={"w-[241px] h-[156px]"}
                name={"Refi + Rehab"}
                icon={<Keys width={60} height={60} />}
                description={
                  "Refinancing option for rehab projects, aimed at maximizing ROI"
                }
                ltc={"85%"}
                interest={"from 11.00%"}
                origination={"from 1.50%"}
                noCta={true}
              />
            </ScrollView>
          </Vertical>
          <Tiny color={"primary"} className={"mt-3"}>
            *Eligibility is based on self-reported information provided. Final
            verification of qualifications will be required.
          </Tiny>
          <Vertical className={"my-5 gap-5"}>
            <PrimaryButton
              title={"Return Home"}
              testID={"home"}
              onPress={() => {
                bottomSheetModalRef.current?.dismiss()
                onClose(false)
                router.push("/")
              }}
            />
            {qualifiedForContactLink && (
              <Pressable
                testID={"contact-us"}
                onPress={() => {
                  loanProductsQuestionClicked("Loan Property Search")
                  void Linking.openURL(
                    "https://backflip.app.link/e/WBWTyZcnSLb"
                  )
                }}
              >
                <H5 className={"underline text-center"} color={"primaryLight"}>
                  Speak with a lending expert
                </H5>
              </Pressable>
            )}
          </Vertical>
        </Vertical>
      </BottomSheetView>
    </BottomSheetModal>
  )
}
